import { Box, Skeleton, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { BenefitDTOBenefitEnum, BikeleasingNewsDTO } from 'probonio-shared-ui/api';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BenefitPaper } from '../../component/benefitTabs/BenefitPaper';
import { getBenefitIcon } from '../../module/benefits/BenefitIcon';
import BenefitSubPage from '../../module/benefits/BenefitSubPage';
import { useBikeleasingNewsArticle } from '../../module/benefits/bikeleasingV2/useBikeleasingNews';
import { useBikeleasingPrecondition } from '../../module/benefits/bikeleasingV2/useBikeleasingPrecondition';
import { AuthorizedLayout } from '../../module/navigation/AuthorizedLayout';

const NewsDetailSkeleton = () => (
  <Box>
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1, width: '50%', mx: 'auto', display: 'block', mb: 2 }} />

    <Skeleton width="20%" />
    <Skeleton width="40%" height={40} sx={{ mb: 1 }} />

    <Skeleton />
    <Skeleton />
    <Skeleton width="80%" />
  </Box>
);

const NewsDetailContent = ({ newsItem }: { newsItem: BikeleasingNewsDTO }) => {
  return (
    <Box>
      {newsItem.image && (
        <Box
          component="img"
          src={newsItem.image.url}
          alt={newsItem.image.alternative}
          sx={{
            maxHeight: 400,
            objectFit: 'cover',
            borderRadius: 1,
            mx: 'auto',
            display: 'block',
            mb: 2,
          }}
        />
      )}

      {newsItem.published?.date && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {DateTime.fromISO(newsItem.published.date).toLocaleString(DateTime.DATE_FULL)}
        </Typography>
      )}

      <Typography variant="h5" fontWeight={600}>
        {newsItem.title}
      </Typography>

      {newsItem.bodytext && (
        <Box
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(newsItem.bodytext),
          }}
        />
      )}
    </Box>
  );
};

const BikeleasingNewsDetailsPage: React.FC = () => {
  const { t } = useTranslation('extensions');
  const { articleId } = useParams();
  const { PreconditionComponent, isPreconditionMet } = useBikeleasingPrecondition();
  const { data: newsItem, isLoading } = useBikeleasingNewsArticle(articleId!);
  const benefit = BenefitDTOBenefitEnum.BikeleasingV2;

  return (
    <AuthorizedLayout
      title={t('bikeleasing_v2.information.news.title')}
      breadcrumbs={[
        { title: t(`benefitsModule:${benefit}.title`), to: `/benefits/${benefit.toLowerCase()}` },
        { title: t(`extensions:bikeleasing_v2.information.news.title`), to: `/benefits/${benefit.toLowerCase()}/articles` },
      ]}
    >
      <BenefitSubPage
        icon={getBenefitIcon(BenefitDTOBenefitEnum.BikeleasingV2)}
        title={t('bikeleasing_v2.title')}
        subTitle={t('bikeleasing_v2.information.news.title')}
      >
        {isPreconditionMet ? (
          <BenefitPaper>
            {isLoading && <NewsDetailSkeleton />}
            {newsItem && !isLoading && <NewsDetailContent newsItem={newsItem} />}
            {!isLoading && !newsItem && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mx: 'auto', my: 2 }}>
                {t('bikeleasing_v2.information.news.noData.details')}
              </Typography>
            )}
          </BenefitPaper>
        ) : (
          <BenefitPaper>{PreconditionComponent}</BenefitPaper>
        )}
      </BenefitSubPage>
    </AuthorizedLayout>
  );
};

export default BikeleasingNewsDetailsPage;
